import { Injectable } from "@angular/core";
import { Mutex } from "async-mutex";
import { AuthTokenMeta, IAMObject } from "../model";
import { LocalStorageKey } from "src/app/utils";
import { AuthenticationService, LocalStorageService } from "../../services.module";
import { DateUtils } from "@yupma/ui-kit";

@Injectable()
export class IAMService {
    private MAX_REFRESH_TOKEN_RETRY = 3;

    private refreshTokenMutex = new Mutex();
    private iamObject: IAMObject | null = null;
    private authTokenMeta: AuthTokenMeta | null = null;
    // Track if we're in the process of trying to refresh a token
    private isRefreshInProgress = false;

    constructor(
        private authenticationService: AuthenticationService,
        private localStorageService: LocalStorageService,
    ) {
        this.onAuthStateChanged();
        // Load cached data on initialization
        this.getIAMObject();
    }

    checkAuthTokenValidity() {
        if (this.authTokenMeta && this.authTokenMeta.authToken) {
            // Add a buffer to ensure we refresh before token actually expires
            // Using 40 minutes instead of 45 to provide a 5-minute buffer
            return DateUtils.getCurrentTimeStamp() - this.authTokenMeta.authTokenCreationTimeStamp < 40 * 60 * 1000; //40min
        } else {
            return false;
        }
    }

    async refreshToken(): Promise<boolean> {
        const release = await this.refreshTokenMutex.acquire();
        let retryCount = 0;
        
        try {
            // Check if we already have a valid token
            if (this.checkAuthTokenValidity()) {
                return true;
            }
            
            // Set flag that refresh is in progress
            this.isRefreshInProgress = true;
            
            while (retryCount < this.MAX_REFRESH_TOKEN_RETRY) {
                try {
                    const token = await this.authenticationService.getUIDToken();
                    if (token) {
                        this.authTokenMeta = new AuthTokenMeta(token);
                        await this.setAuthTokenToCache(); // Ensure this completes before returning
                        return true;
                    }
                    retryCount++;
                    if (retryCount < this.MAX_REFRESH_TOKEN_RETRY) {
                        await this.sleep(Math.pow(2, retryCount) * 500); // Exponential backoff
                    }
                } catch (error) {
                    retryCount++;
                    console.error(`Token refresh attempt ${retryCount} failed:`, error);
                    if (retryCount < this.MAX_REFRESH_TOKEN_RETRY) {
                        await this.sleep(Math.pow(2, retryCount) * 500); // Exponential backoff
                    }
                }
            }
            
            // If we've exhausted all retries, clear the token to force a fresh login
            if (retryCount >= this.MAX_REFRESH_TOKEN_RETRY) {
                console.error("Maximum token refresh retries reached.");
                
                // Don't clear immediately - let the caller decide what to do
                // this.authTokenMeta = null;
                // await this.setAuthTokenToCache();
            }
            
            return false;
        } finally {
            this.isRefreshInProgress = false;
            release();
        }
    }

    async setIAMObject(iamObject: IAMObject | null): Promise<void> {
        try {
            this.iamObject = iamObject;
            if (this.iamObject) {
                await this.setAuthTokenToCache();
            } else {
                // Clear token meta when IAM object is cleared
                this.authTokenMeta = null;
                await this.setAuthTokenToCache();
            }
            //Update to cookies/caching service
            await this.localStorageService.setItem(LocalStorageKey.IAM_OBJECT, this.iamObject);
        } catch (error) {
            console.error("Error setting IAM object:", error);
        }
    }

    getIAMObject(): IAMObject | null {
        if (!this.iamObject) {
            this.iamObject = this.localStorageService.getItem(LocalStorageKey.IAM_OBJECT);
            this.getAuthTokenFromCache();
        }
        return this.authTokenMeta && this.authTokenMeta.authToken && this.iamObject ? this.iamObject : null;
    }

    private async onAuthStateChanged(): Promise<void> {
        this.authenticationService.onUserAuthStateChangedEvent$.subscribe(async (token: string) => {
            if (token) {
                this.authTokenMeta = new AuthTokenMeta(token);
                await this.setAuthTokenToCache();
            } else {
                await this.setIAMObject(null);
            }
        });
    }

    getAuthToken(): string | undefined {
        if (!this.authTokenMeta) {
            this.getAuthTokenFromCache();
        }
        return this.authTokenMeta?.authToken;
    }

    private async setAuthTokenToCache() {
        try {
            //Update to cookies/caching service
            await this.localStorageService.setItem(LocalStorageKey.AUTH_TOKEN_META, this.authTokenMeta);
        } catch (error) {
            console.error("Error setting auth token to cache:", error);
        }
    }

    private getAuthTokenFromCache() {
        this.authTokenMeta = this.localStorageService.getItem(LocalStorageKey.AUTH_TOKEN_META);
    }

    getOrganisationId() {
        // Make sure IAM object is loaded
        this.getIAMObject();
        return this.iamObject ? this.iamObject.organsiation_id : '';
    }

    getOrganisationName() {
        // Make sure IAM object is loaded
        this.getIAMObject();
        return this.iamObject ? this.iamObject.organsiation_name : '';
    }

    getUserId() {
        // Make sure IAM object is loaded
        this.getIAMObject();
        return this.iamObject ? this.iamObject.uid : '';
    }

    // Improved sleep with configurable duration
    sleep(ms = 500) {
        return new Promise(resolve => {
            setTimeout(() => { resolve(true) }, ms);
        });
    }
    
    // Method to check if auth is available (token exists and IAM object exists)
    isAuthenticated(): boolean {
        return !!(this.getIAMObject() && this.getAuthToken());
    }
}